/**
 * sequencerRandom — musical pattern generators for the step Sequencer.
 *
 * Random patterns generated by uniformly picking MIDI note numbers
 * sound atonal and unmusical. Most useful "throw the dice" sequences
 * lock notes to a scale, keep them in a playable octave range, leave
 * some rests, and shape the modulation lane as a smooth curve rather
 * than per-step noise. This module bakes those choices in.
 *
 * Scales are stored as semitone-offset arrays from the root. Mode /
 * key changes pick a different array + root; the pattern body only
 * cares about "which degrees of this scale". Scale length doesn't
 * have to be 7 — pentatonic and chromatic both round-trip the same
 * way.
 */

import type { SequencerStep } from './Sequencer';
import { SEQ_MAX_STEPS } from './Sequencer';

export type ScaleName =
  // Pentatonics — five-note scales, hard to land on a wrong note.
  | 'minor-pentatonic'
  | 'major-pentatonic'
  | 'blues'
  | 'hirajoshi'
  // Diatonic modes — the seven modes of the major scale.
  | 'major'
  | 'minor'
  | 'dorian'
  | 'phrygian'
  | 'lydian'
  | 'mixolydian'
  | 'locrian'
  // Outside the diatonic family — exotic / colorful character.
  | 'harmonic-minor'
  | 'melodic-minor'
  | 'phrygian-dominant'
  | 'whole-tone'
  | 'diminished'
  | 'chromatic';

/** Semitone offsets from the root for each scale. The root is always
 *  index 0; chromatic includes every semitone for users who want a
 *  fully unconstrained pattern. New scales added at the bottom of
 *  the modes / exotics blocks so the order in the picker reads from
 *  "consonant" → "exotic" → "everything goes". */
const SCALES: Readonly<Record<ScaleName, readonly number[]>> = {
  // Pentatonics
  'minor-pentatonic': [0, 3, 5, 7, 10],
  'major-pentatonic': [0, 2, 4, 7, 9],
  // Minor pentatonic + the blue note (♭5).
  blues: [0, 3, 5, 6, 7, 10],
  // Japanese pentatonic, a moody minor-pentatonic-like scale.
  hirajoshi: [0, 2, 3, 7, 8],

  // Modes
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  // Major with #4 — dreamy / "Star Trek" feel.
  lydian: [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  // Diminished feel — minor with ♭2 and ♭5.
  locrian: [0, 1, 3, 5, 6, 8, 10],

  // Exotics
  // Minor with raised 7th — "Middle Eastern" / horror-movie minor.
  'harmonic-minor': [0, 2, 3, 5, 7, 8, 11],
  // Minor with raised 6 + 7 — jazz minor (ascending form).
  'melodic-minor': [0, 2, 3, 5, 7, 9, 11],
  // Phrygian with major 3rd — flamenco / metal.
  'phrygian-dominant': [0, 1, 4, 5, 7, 8, 10],
  // Symmetric, no leading tone — Debussy / dream sequences.
  'whole-tone': [0, 2, 4, 6, 8, 10],
  // Half-whole diminished — jazz / dissonant.
  diminished: [0, 1, 3, 4, 6, 7, 9, 10],

  // Anything goes
  chromatic: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
};

export const SCALE_NAMES: readonly ScaleName[] = [
  // Pentatonics first — most "throw the dice and it sounds OK" scales.
  'minor-pentatonic',
  'major-pentatonic',
  'blues',
  'hirajoshi',
  // Modes, brightest → darkest.
  'major',
  'lydian',
  'mixolydian',
  'dorian',
  'minor',
  'phrygian',
  'locrian',
  // Exotic / colorful flavors.
  'harmonic-minor',
  'melodic-minor',
  'phrygian-dominant',
  'whole-tone',
  'diminished',
  // Fully unconstrained.
  'chromatic',
];

export interface MusicalRandomOptions {
  /** Scale to draw notes from. Defaults to minor-pentatonic — virtually
   *  everything sampled from it sounds at least passable. */
  readonly scale?: ScaleName;
  /** Root note as a MIDI number. Default 60 (C4). The scale array is
   *  spread upward from here over `octaveRange` octaves. */
  readonly rootNote?: number;
  /** How many octaves above the root to populate the note pool.
   *  Default 2. Larger = wider melodic reach. */
  readonly octaveRange?: number;
  /** Probability that a step is active. Default 0.7 — frequent enough
   *  that the pattern reads as a melody, sparse enough to leave room
   *  for the rhythm to breathe. */
  readonly density?: number;
  /** Probability that an active step ties forward (length 2 or more)
   *  rather than firing single-step. Default 0.3 — frequent enough
   *  that ties read as a deliberate part of the pattern, sparse
   *  enough that the rhythm still grooves. */
  readonly tieProbability?: number;
  /** Maximum tie length in steps. The actual length on each tied
   *  step is sampled from `[2, maxTieLength]` with a bias toward
   *  shorter ties. Default 4. */
  readonly maxTieLength?: number;
  /** Number of steps to generate. Defaults to SEQ_MAX_STEPS so the
   *  full buffer is filled — the playback `length` parameter clips
   *  what's actually heard. */
  readonly stepCount?: number;
  /** Optional seeded RNG for deterministic tests; defaults to Math.random. */
  readonly rng?: () => number;
}

/**
 * Build a fresh `SequencerStep[]` of length `stepCount`. Notes are
 * scale-locked, velocities cluster around 0.7 with light variation,
 * lengths are usually 1 with the occasional tie, and the mod lane
 * is shaped as a smoothed random walk so a routed parameter moves
 * organically rather than zig-zagging every step.
 */
export function generateMusicalSequence(
  opts: MusicalRandomOptions = {},
): SequencerStep[] {
  const scaleName = opts.scale ?? 'minor-pentatonic';
  const root = clampMidi(opts.rootNote ?? 60);
  const octaves = Math.max(1, Math.min(4, Math.round(opts.octaveRange ?? 2)));
  const density = clamp01(opts.density ?? 0.7);
  const tieProb = clamp01(opts.tieProbability ?? 0.3);
  // Cap maxTieLength to SEQ_MAX_STEPS so a tie can't exceed what
  // setStep would later clamp to. 4 steps is a half-bar at 1/16
  // — enough to read as a deliberate held note, short enough that
  // shorter notes still get a turn.
  const maxTieLength = Math.max(2, Math.min(SEQ_MAX_STEPS, opts.maxTieLength ?? 4));
  const count = Math.max(1, Math.min(SEQ_MAX_STEPS, opts.stepCount ?? SEQ_MAX_STEPS));
  const rng = opts.rng ?? Math.random;

  // Build the playable note pool: scale × octaveRange octaves above
  // the root. Filter into MIDI's [0,127] window — high octaves with a
  // C8 root would otherwise overflow.
  const scale = SCALES[scaleName];
  const pool: number[] = [];
  for (let oct = 0; oct < octaves; oct++) {
    for (const offset of scale) {
      const n = root + oct * 12 + offset;
      if (n >= 0 && n <= 127) pool.push(n);
    }
  }
  if (pool.length === 0) pool.push(root); // fallback: unconstrained edge case

  // Mod lane shape — smoothed random walk. Each step adds a small
  // delta to the previous value and clamps to [0,1]. Produces a
  // naturalistic drift rather than zig-zag noise so the routed
  // parameter actually evolves over the bar.
  const modWalk = generateSmoothWalk(count, rng);

  const out: SequencerStep[] = [];
  // Lookahead state: when we generate a tie, the next step(s) get
  // active=false so playback doesn't trigger over the held note.
  let tieRemaining = 0;
  for (let i = 0; i < count; i++) {
    if (tieRemaining > 0) {
      // Carrier slot for a tie — silent, but the mod value still
      // matters and the note doesn't either way.
      out.push({
        active: false,
        note: pool[Math.floor(rng() * pool.length)] ?? root,
        velocity: 0.7,
        length: 1,
        modValue: modWalk[i] ?? 0.5,
      });
      tieRemaining--;
      continue;
    }

    const active = rng() < density;
    const note = pool[Math.floor(rng() * pool.length)] ?? root;
    // Velocity centered at 0.75 with ±0.2 jitter. Pure-random 0..1
    // velocity sounds "video game" — clustering closer to a sane
    // performance level reads as more musical.
    const velocity = clamp01(0.75 + (rng() * 2 - 1) * 0.2);
    let length = 1;
    if (active && rng() < tieProb && i + 1 < count) {
      // Sample a tie length in [2, maxTieLength] biased toward the
      // short end — squaring the [0,1] roll skews most picks toward
      // 2, with the occasional longer hold. Capped at the room
      // remaining in the bar so a tie can't run past the loop edge.
      const range = Math.max(0, maxTieLength - 2);
      const skew = rng() * rng(); // 0..1, biased toward 0
      const sampled = 2 + Math.floor(skew * (range + 1));
      const room = count - i - 1; // steps available after this one
      length = Math.min(sampled, 1 + room);
      if (length < 2) length = 1;
      else tieRemaining = length - 1;
    }
    out.push({
      active,
      note,
      velocity,
      length,
      modValue: modWalk[i] ?? 0.5,
    });
  }
  return out;
}

/** Smoothed [0,1] random walk over `n` samples. Used for the mod lane
 *  so routed parameters drift instead of jumping. Step size is
 *  bounded so the curve traces the full range without snapping. */
function generateSmoothWalk(n: number, rng: () => number): number[] {
  const out: number[] = [];
  let v = rng();
  for (let i = 0; i < n; i++) {
    out.push(clamp01(v));
    // Step size capped at 0.25 so the walk takes ~4 steps to cross
    // the full range — long enough to read as movement, short
    // enough to not feel glacial on a 16-step bar.
    const delta = (rng() * 2 - 1) * 0.25;
    v = clamp01(v + delta);
  }
  return out;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function clampMidi(v: number): number {
  return Math.max(0, Math.min(127, Math.round(v)));
}
